import { onMounted } from 'vue'
import { useRoute } from 'vue-router'

import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useSite } from '@/composables/core/useSite'
import { useAnnouncementQueue } from '@/composables/ui/useAnnouncementQueue'
import { useMainStore } from '@/stores/main'
import { MODAL_INTENT, hasErrorParam, resolveModalComponent, shouldOpenModalOnEntry } from '@/utils/urlModal'

// 網址觸發彈窗的頁面共用入口：/register、/login、/guest/register、/guest/login
// 規格：spec/JT-1784716920_url-modal-trigger-announcement-deferral.md §5.1（D7）
//
// 開窗留在頁面層而非 middleware：middleware 可能在 siteConfig 就緒前執行，
// 且直接進 /register 本來就由頁面負責開窗，統一在此避免兩套入口
export const useUrlModalTrigger = (intent) => {
  const route = useRoute()
  const { isLoggedIn } = useAuthToken()
  const { setModal, setUrlModalPending } = useMainStore()
  const { flushDeferredAnnouncement } = useAnnouncementQueue()
  // useSite() 內含 useFetch 與 onMounted 註冊，必須在 setup 同步階段呼叫：
  // 放進 onMounted callback 時單例若尚未建立，useFetch 會在 setup 外執行而失去 SSR payload，
  // 其內部的 onMounted 也因元件已掛載而永不觸發（目前靠 layout 先呼叫過才沒事，屬隱性耦合）
  const { getSiteConfig, hasRegister, fetchSiteConfig } = useSite()

  onMounted(async () => {
    // 已登入由 middleware 導 /main；帶 ?error= 時只顯示錯誤彈窗（規格第 8 節）
    // 不開窗一律走 flush 而非只清旗標：旗標可能已由 middleware 設定並讓公告進了暫存，
    // 只清旗標會讓那批公告永遠留在暫存裡（沒有暫存時 flush 等同清旗標）
    if (!shouldOpenModalOnEntry({ intent, hasError: hasErrorParam(route.query), isLoggedIn: isLoggedIn.value })) {
      flushDeferredAnnouncement()
      return
    }

    // 站台未開放註冊時 register 需 fallback 開登入窗，判斷前必須先有 siteConfig
    if (intent === MODAL_INTENT.REGISTER && Object.keys(getSiteConfig.value || {}).length === 0) {
      // siteConfig 取不到時 hasRegister 為 0，resolveModalComponent 會 fallback 開登入面板，
      // 不應以未捕捉錯誤中斷開窗；useSite 的 onResponseError 已 console.error 並於 request 層上報
      await fetchSiteConfig().catch(() => {})
    }

    const modalName = resolveModalComponent({ intent, hasRegisterMethod: Boolean(hasRegister.value) })
    if (!modalName) {
      flushDeferredAnnouncement()
      return
    }

    // 公告延後旗標由 middleware 於路由階段設定；直接進 /register 這類未經 middleware
    // 正規化的路徑同樣需要標記，避免公告與彈窗同時開啟
    setUrlModalPending(intent)
    setModal(modalName)
  })
}
